/**
 * Watermark service — thin react-query wrappers around the
 * Wave 6 SECURITY DEFINER RPCs and the studio-side CRUD.
 *
 * Two audiences:
 *
 *   1. Client-facing pages (PublicDelivery, PortalGalleryTab,
 *      ClientGalleryView) call `useGalleryWatermark` /
 *      `useDeliveryWatermark`. Returns null when the surface hasn't
 *      opted in or the studio has no configured mark — the overlay
 *      + downloadZip both no-op on null.
 *
 *   2. Studio Settings page reads `useStudioWatermarkSettings` and
 *      writes via `useSaveStudioWatermarkSettings`. Standard RLS'd
 *      upsert against `studio_watermark_settings`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import type { WatermarkConfig } from '@/components/watermark/WatermarkOverlay';

export type { WatermarkConfig } from '@/components/watermark/WatermarkOverlay';

export interface StudioWatermarkSettings {
  org_id: string;
  image_url: string | null;
  text: string | null;
  position: WatermarkConfig['position'];
  opacity: number;
  scale: number;
  created_at: string;
  updated_at: string;
}

// ─── Client-facing (anon-safe) lookups ────────────────────────────

/**
 * Watermark config for a gallery, or null when the gallery has
 * watermark_enabled=false or the parent studio has no config.
 * Returns null while `galleryId` is undefined so callers can bind
 * to a still-loading id without conditional hooks.
 */
export const useGalleryWatermark = (galleryId: string | undefined) => {
  return useQuery({
    queryKey: ['gallery-watermark', galleryId],
    queryFn: async (): Promise<WatermarkConfig | null> => {
      if (!galleryId) return null;
      const { data, error } = await (supabase as any).rpc('get_gallery_watermark', {
        p_gallery_id: galleryId,
      });
      if (error) throw error;
      return (data ?? null) as WatermarkConfig | null;
    },
    enabled: !!galleryId,
    // Static for the page lifetime — the studio changing their
    // watermark mid-view is exceedingly rare and a refresh handles it.
    staleTime: Infinity,
  });
};

/** Delivery equivalent. Called after unlock_public_delivery
 *  succeeds so we already have a delivery id. */
export const useDeliveryWatermark = (deliveryId: string | undefined) => {
  return useQuery({
    queryKey: ['delivery-watermark', deliveryId],
    queryFn: async (): Promise<WatermarkConfig | null> => {
      if (!deliveryId) return null;
      const { data, error } = await (supabase as any).rpc('get_delivery_watermark', {
        p_delivery_id: deliveryId,
      });
      if (error) throw error;
      return (data ?? null) as WatermarkConfig | null;
    },
    enabled: !!deliveryId,
    staleTime: Infinity,
  });
};

// ─── Studio-side settings ─────────────────────────────────────────

export const useStudioWatermarkSettings = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['studio-watermark-settings', orgId],
    queryFn: async (): Promise<StudioWatermarkSettings | null> => {
      if (!orgId) return null;
      const { data, error } = await (supabase as any)
        .from('studio_watermark_settings')
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as StudioWatermarkSettings | null;
    },
    enabled: !!orgId,
  });
};

export const useSaveStudioWatermarkSettings = () => {
  const { data: orgId } = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      patch: Partial<Pick<
        StudioWatermarkSettings,
        'image_url' | 'text' | 'position' | 'opacity' | 'scale'
      >>,
    ): Promise<StudioWatermarkSettings> => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const { data, error } = await (supabase as any)
        .from('studio_watermark_settings')
        .upsert(
          { org_id: orgId, ...patch, updated_at: new Date().toISOString() },
          { onConflict: 'org_id' },
        )
        .select('*')
        .single();
      if (error) throw error;
      return data as StudioWatermarkSettings;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio-watermark-settings'] });
      // Per-surface caches (client + studio) — the mark could have
      // changed shape / image; every ClientGalleryView caching a
      // config becomes stale.
      qc.invalidateQueries({ queryKey: ['gallery-watermark'] });
      qc.invalidateQueries({ queryKey: ['delivery-watermark'] });
    },
  });
};

/**
 * Upload the watermark PNG to brand-assets/${org_id}/watermark.png.
 * Overwrites any existing file (upsert) so studios can iterate
 * without cache-busting. Returns the public URL for the caller to
 * persist into studio_watermark_settings.image_url.
 */
export async function uploadWatermarkPng(
  orgId: string,
  file: File,
): Promise<string> {
  // Timestamp suffix so browsers pull the fresh version — brand-
  // assets serves with immutable cache headers on most CDNs.
  const stamp = Date.now();
  const path = `${orgId}/watermark-${stamp}.png`;
  const { error } = await supabase.storage
    .from('brand-assets')
    .upload(path, file, { upsert: true, contentType: 'image/png' });
  if (error) throw error;
  const { data } = supabase.storage.from('brand-assets').getPublicUrl(path);
  return data.publicUrl;
}
